import { useHotkey } from "@tanstack/react-hotkeys"
import type { IRowNode } from "ag-grid-community"
import { useEffect, useLayoutEffect, useRef, useState } from "react"

import {
  useHighlightStepStore,
  type HoveredRowActions,
} from "~/components/design-system/pages/internal/inspectorHighlight"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import { HOTKEYS } from "~/constants/hotkeys"
import { isEditableElement, isInDialog } from "~/utils/helpers"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"

/**
 * Whether the pointer has entered `rowNode` yet; stays true. ag-grid sets `.ag-row-hover` from this
 * same row event, so a control mounted on it appears as the CSS reveals it.
 */
export const useWasRowHovered = (rowNode: IRowNode): boolean => {
  const [hovered, setHovered] = useState(() => rowNode.isHovered())
  useEffect(() => {
    if (hovered) return
    const onEnter = () => setHovered(true)
    if (rowNode.isHovered()) onEnter()
    rowNode.addEventListener("mouseEnter", onEnter)
    return () => rowNode.removeEventListener("mouseEnter", onEnter)
  }, [rowNode, hovered])
  return hovered
}

/**
 * Publishes `actions` as the hovered row's while the pointer is over `rowNode`, for
 * {@link useHoveredRowHotkeys}. Read at press time, so fresh closures each render are fine.
 */
export const usePublishHoveredRow = (rowNode: IRowNode, actions: HoveredRowActions) => {
  const actionsRef = useRef(actions)
  useLayoutEffect(() => {
    actionsRef.current = actions
  })
  useEffect(() => {
    const { setHoveredRow, withdrawHoveredRow } = useHighlightStepStore.getState()
    const publish = () => setHoveredRow(actionsRef)
    const withdraw = () => withdrawHoveredRow(actionsRef)
    if (rowNode.isHovered()) publish()
    rowNode.addEventListener("mouseEnter", publish)
    rowNode.addEventListener("mouseLeave", withdraw)
    return () => {
      rowNode.removeEventListener("mouseEnter", publish)
      rowNode.removeEventListener("mouseLeave", withdraw)
      withdraw()
    }
  }, [rowNode])
}

const readHoveredRow = () => useHighlightStepStore.getState().hoveredRow?.current ?? null

/**
 * Whether the row under the pointer opens elsewhere on `HOTKEYS.openHoveredRow`. A table with its
 * own binding on that key checks this first, so one press never opens two items.
 */
export const isHoveredRowOpenable = () => readHoveredRow()?.openElsewhere != null

const skipKey = (event: KeyboardEvent) =>
  isEditableElement(document.activeElement) || isInDialog(event.target)

/**
 * The hovered row's shortcuts, as its buttons would act: `I` opens the inspector on it (or closes
 * it when that row is already on show), `Shift+O` presses its ↗. With no row hovered, `I` presses
 * the toolbar's show/hide toggle (`toggleInspector`, absent when there is none). One binding each
 * per Module, so the shortcuts dialog lists them once. Mounted by `ModuleInspector`.
 */
export const useHoveredRowHotkeys = (enabled: boolean, toggleInspector?: () => void) => {
  const isCoveredByDialog = useIsCoveredByFullPageDialog()
  const active = enabled && !isCoveredByDialog
  useHotkey(
    HOTKEYS.inspectHoveredRow.hotkey,
    (event) => {
      const action = readHoveredRow()?.toggle ?? toggleInspector
      if (!action || skipKey(event)) return
      event.preventDefault()
      action()
    },
    {
      enabled: active,
      meta: shortcutMeta("Tables", "Inspect hovered row, or show/hide inspector"),
    },
  )
  useHotkey(
    HOTKEYS.openHoveredRow.hotkey,
    (event) => {
      const open = readHoveredRow()?.openElsewhere
      if (!open || skipKey(event)) return
      event.preventDefault()
      open()
    },
    { enabled: active, meta: shortcutMeta("Tables", "Open hovered row") },
  )
}
